import { useEffect, useState, useRef } from "react";
import { Wheel } from "../../app/utils/react-custom-roulette/components/Wheel";
import "./SpinWheel.css";
import SpinEffect from "./SpinEffect";
import SpinSlider from "./SpinSlider";
import { WheelProps } from "../../app/models/wheelProps";
import { getWinningSlice } from "../../app/utils/getWinningSlice";
import Swal from "sweetalert2";
import {v4 as uuid} from "uuid";
import agent from "../../app/api/agent";
import { Player } from "../../app/models/player";
import { env } from "../../app/utils/env";
import { PointerPin, WheelContainer, WheelStyles } from "./wheelStyles";
import { validateImageUrl } from "../../app/utils/validateImages";
import { Slice } from "../../app/models/slice";
import { FormStateProps } from "../../app/models/formStateProps";
import CurvedSlider from "./CurvedSlider";
import UserDataModal, { UserDataValues } from "../UserDataModal";
import { toast } from "react-toastify";
import { ModalConfig } from "../../app/models/modalConfig";

interface SpinWheelProps {
  isActive?: boolean;
  collectUserData: number;
  wheel: WheelProps['wheel'];
  slices: WheelProps['slices'];
  formState: FormStateProps['formState'];
  campaignId: string;
  onStockDepletion: () => void;
  modalConfig: ModalConfig;
  remote?: { enabled: boolean; player: any | null; endGame: () => void };
  onRemotePlayStarted?: () => void;
}

const SpinWheel: React.FC<SpinWheelProps> = ({ isActive, collectUserData, wheel, slices, formState, campaignId, onStockDepletion, modalConfig, remote, onRemotePlayStarted }) => {
  const [mustSpin, setMustSpin] = useState(false);
  const [readyToSpin, setReadyToSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [effect, setEffect] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [resetWheel, setResetWheel] = useState(false);
  const [player, setPlayer] = useState<Player | null>(null);
  const [slicesState, setSlicesState] = useState(slices);

  const initialFormValues: any = {
    firstName: "",
    lastName: "",
    email: formState.formEmail ? "" : undefined,
    phone: formState.formPhone ? "" : undefined,
    company: formState.formCompany ? "" : undefined,
    gender: formState.formGender ? 1 : undefined,
    formAddPermissionChecked: false,
    formAddPermission2Checked: false,
    ...(formState.bonusField && { [formState.bonusField]: "" }),
  };
  const [formValues, setFormValues] = useState(initialFormValues);

  const [dataCollected, setDataCollected] = useState(false);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(true);

  const [wheelSlide, setWheelSlide] = useState({ clockwise: true, spinSpeed: 1 });

  let playerId = null;

  const nonNullSlices = slicesState.filter(slice => slice.probability !== null);
  const areAllProbabilitiesZero = nonNullSlices.length > 0 && nonNullSlices.every(slice => slice.probability === 0);

  const audioRef = useRef(new Audio(`${process.env.PUBLIC_URL}/assets/wheel-turn-effect.mp3`));

  const handleSpinSlider = (direction: 'clockwise' | 'counterclockwise', spinSpeed: number) => {
    if (!mustSpin) {
      setWheelSlide({
        clockwise: direction === 'clockwise',
        spinSpeed,
      });
      const validSlices = slicesState.filter(slice => slice.probability! > 0);
      let winnerId: string;
      if (validSlices.length > 0) {
        winnerId = getWinningSlice(validSlices);
      } else {
        winnerId = getWinningSlice(slicesState);
      }
      
      const winnerSlice = slicesState.find(slice => slice.id == winnerId);
      const newPrizeNumber = slicesState.indexOf(winnerSlice!);
      setPrizeNumber(newPrizeNumber);

      // inform kiosk that game started to cancel 60s timeout
      if (collectUserData === 4) onRemotePlayStarted?.();

      setMustSpin(true);
    }
  };

  const handleSpinClick = () => {
    setEffect(false);
    if (!mustSpin) {
      const validSlices = slicesState.filter(slice => slice.probability! > 0);
      let winnerId: string;
      if (validSlices.length > 0) {
        winnerId = getWinningSlice(validSlices);
      } else {
        winnerId = getWinningSlice(slicesState);
      }
      
      const winnerSlice = slicesState.find(slice => slice.id == winnerId);
      const newPrizeNumber = slicesState.indexOf(winnerSlice!);
      setPrizeNumber(newPrizeNumber);

      // inform kiosk that game started to cancel 60s timeout
      if (collectUserData === 4) onRemotePlayStarted?.();

      setMustSpin(true);
    }
  };

  useEffect(() => {
    if (collectUserData === 4 && remote?.enabled && remote.player) {
      setPlayer(remote.player);
      setDataCollected(true);
      setOpenModal(false);
    }
  }, [collectUserData, remote?.player, remote?.enabled]);

  useEffect(() => {
    if (mustSpin) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [mustSpin]);

  useEffect(() => {
    if (!isActive || areAllProbabilitiesZero) {
      Swal.fire({
        title: "Oyun Bitti",
        text: "Oyun sona erdi. Katıldığınız için teşekkürler.",
        icon: "warning",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showCloseButton: false,
        showConfirmButton: false,
      });
  
      setMustSpin(false);
    }
  }, [slicesState]);

  useEffect(() => {
    const validateImages = async () => {
      const updatedSlices: Slice[] = await Promise.all(
        slicesState.map(async (slice): Promise<Slice> => {
          const isValidImage = slice.wedgeImage
            ? await validateImageUrl(slice.wedgeImage.startsWith('http') ? slice.wedgeImage : env + slice.wedgeImage)
            : false;
  
          return {
            ...slice,
            wedgeImage: isValidImage ? slice.wedgeImage : "",
          };
        })
      );
  
      setSlicesState(updatedSlices);  // Update slices state with validated images
      setValidated(true);  // Mark validation as complete
      setLoading(false);    // Allow the Wheel to be rendered
    };
  
    if (!validated) {
      validateImages();
    }
  }, [slicesState, validated]);


  const handleClose = () => {
    if (collectUserData != 2 && !dataCollected) {
      setPlayer(null);
    }
    setOpenModal(false);
    setResetWheel(true);
    setEffect(true);
    setFormValues({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      gender: 1,
      formAddPermissionChecked: false,
      formAddPermission2Checked: false,
    });
  };

  useEffect(() => {
    if (resetWheel) {
      setMustSpin(false);
      setResetWheel(false);
      setReadyToSpin(false);
    }
  }, [resetWheel]);

  const buildPlayerData = (formValues: any, formState: FormStateProps['formState'], campaignId: string, playerId: string) => {
    return {
      id: playerId,
      campaignId,
      firstName: formValues.firstName || "",
      lastName: formValues.lastName || "",
      gender: formState.formGender ? formValues.gender === 1 ? 1 : 2 : 0,
      email: formState.formEmail ? formValues.email || "" : "",
      phone: formState.formPhone ? formValues.phone || "" : "",
      company: formState.formCompany ? formValues.company || "" : "",
      formAddPermissionChecked: formState.formAddPermission ? formValues.formAddPermissionChecked || false : false,
      formAddPermission2Checked: formState.formAddPermission2 ? formValues.formAddPermission2Checked || false : false,
      bonusField: formState.bonusField ? formValues[formState.bonusField] || "" : "",
      bonusFieldLabel: collectUserData != 1 ? formState.bonusField || "" : "",
    };
  };

  const buildPlayerFromRemote = (remotePlayer: any): Player => {
    const id = remotePlayer.id ?? uuid();
    return {
      id,
      campaignId,
      firstName: remotePlayer.firstName ?? "",
      lastName: remotePlayer.lastName ?? "",
      gender: formState.formGender ? (remotePlayer.gender === 1 ? 1 : 2) : 0,
      email: formState.formEmail ? (remotePlayer.email ?? "") : "",
      phone: formState.formPhone ? (remotePlayer.phone ?? "") : "",
      company: formState.formCompany ? (remotePlayer.company ?? "") : "",
      formAddPermissionChecked: formState.formAddPermission ? (remotePlayer.formAddPermissionChecked || false) : false,
      formAddPermission2Checked: formState.formAddPermission2 ? (remotePlayer.formAddPermission2Checked || false) : false,
      bonusField: formState.bonusField ? (remotePlayer[formState.bonusField] ?? "") : "",
      bonusFieldLabel: collectUserData != 1 ? formState.bonusField || "" : "",
    };
  };

  const onWheelStop = async () => {
    setMustSpin(false);
    setReadyToSpin(true);

    const selectedSlice = slices[prizeNumber];
    const oldStock = slicesState[prizeNumber].stock;

    let effectivePlayer: Player | null = null;

    Swal.fire({
      title: selectedSlice.prizeValue,
      text: selectedSlice.isPrize ? (modalConfig.modalText ?? `Tebrikler! Hediye kazandınız ${collectUserData === 3 ? "Hediyenizi almak için kayıt oluşturun." : ""}`) : (modalConfig.modalPassText ?? "Katıldığınız için teşekkürler."),
      icon: selectedSlice.isPrize ? "success" : "error",
              imageUrl: selectedSlice.prizeImage ? (selectedSlice.prizeImage.startsWith('http') ? selectedSlice.prizeImage : env + selectedSlice.prizeImage) : undefined,
      imageWidth: 200,
      showCancelButton: collectUserData === 3 ? true : false,
      showConfirmButton: collectUserData === 3 ? true : false,
      confirmButtonText: "Kullanıcı Formu",
      cancelButtonText: "Başa Dön",
      customClass: {
        popup: modalConfig.modalBg ? 'swal-container swal-bg d-bg-hide' : `swal-container ${modalConfig.modalBorderRadius === false ? "no-radius" : ""}`
      },
      background: modalConfig.modalBg ? `url(${modalConfig.modalBg})` : (modalConfig.modalInnerBg ? `url(${modalConfig.modalInnerBg})` : modalConfig.modalColor ?? '#fff'),
      color: modalConfig.modalTextColor ?? "#545454",
      didOpen: () => {
        if (collectUserData != 1) {
          document.addEventListener('click', () => {
            Swal.close();
          });
        }
      },
    }).then(async (result) => {
      if (collectUserData === 4 && remote?.enabled && (remote.player || player)) {
        effectivePlayer = buildPlayerFromRemote(remote?.player ?? player);
      } else if (collectUserData === 2 && player) {
        // modal-before flow already populated `player`
        effectivePlayer = player;
      } else if (collectUserData === 3) {
        const pid = player?.id || uuid();
        effectivePlayer = buildPlayerData(formValues, formState, campaignId, pid) as Player;
      } else if (collectUserData === 1) {
        const pid = uuid();
        effectivePlayer = {
          id: pid,
          campaignId,
          firstName: "",
          lastName: "",
          gender: 0,
          email: "",
          phone: "",
          company: "",
          formAddPermissionChecked: false,
          formAddPermission2Checked: false,
          bonusField: "",
          bonusFieldLabel: "",
        };
      }

      const playerIdToUse = effectivePlayer?.id ?? uuid();

      const gameData = {
        id: uuid(),
        gameType: 1,
        prize: selectedSlice.prizeValue,
        isPrize: selectedSlice.isPrize,
        hasWon: true,
        datePlayed: new Date().toISOString(),
        campaignId,
        playerId: playerIdToUse
      };
      if (oldStock! > 0) {
        const sliceData = {
          ...selectedSlice,
          stock: oldStock! - 1,
          wheelId: wheel.id
        }
        const updatedSlices = slicesState.map(slice => 
          slice.id === sliceData.id 
            ? { ...slice, stock: sliceData.stock }
            : slice
        );
        setSlicesState(updatedSlices);

        await agent.Slices.update(sliceData);

        onStockDepletion();

        // Check and update probability if stock becomes 0
        if (sliceData.stock <= 0) {
          const updatedSlicesWithProbability = updatedSlices.map(slice => 
            slice.id === sliceData.id 
              ? { ...slice, probability: 0 }
              : slice
          );
          setSlicesState(updatedSlicesWithProbability);
          
        }
      }
      if (effectivePlayer) {
        await agent.Games.create({ ...gameData, player: effectivePlayer } as any);
      } else {
        await agent.Games.create(gameData as any);
      }
      setPlayer(effectivePlayer ?? null);
      if (isActive && collectUserData == 3 && result.isConfirmed) {
        setOpenModal(true);
      } else {
        setPlayer(null);
        setResetWheel(true);
        setEffect(true);
        setDataCollected(false);
      }
      if (collectUserData === 4 && remote?.enabled) {
        // notify hub → unlock and kiosk returns to video
        remote.endGame();
      }
    });
  };  

  const hasDuplicatePlayer = async (campaignId: string, values: UserDataValues) => {
    if (!formState.duplicateCheck) return false;
    const res = await agent.Games.exists(campaignId, {
      firstName: values.firstName,
      lastName: values.lastName,
      email: formState.formEmail ? values.email : undefined,
      phone: formState.formPhone ? values.phone : undefined,
    });
    return res.duplicate;
  };

  const handleSubmit = async (values: UserDataValues) => {
    if (await hasDuplicatePlayer(campaignId, values)) {
      toast.error("Aynı oyuncu bir kampanyada sadece bir kez oynayabilir.");
      return false; // keep modal open, do NOT reset anything
    }

    const playerId = player?.id || uuid();
    const playerData = buildPlayerData(values, formState, campaignId, playerId);

    if (collectUserData === 2) {
      setPlayer(playerData);
      setDataCollected(true);
    } else {
      await agent.Players.update(playerData);
    }

    return true;
  };

  const renderSpinMethod = () => {
    switch (wheel.spinningMethod) {
      case 2: // SpinSlider
        return !mustSpin && <SpinSlider onSpin={handleSpinSlider} />;

      case 3: // Button
        return (
          <button
            className="start-button"
            onClick={areAllProbabilitiesZero ? undefined : handleSpinClick}
          >
            <img
              src={wheel.spinButtonImage ? (wheel.spinButtonImage.startsWith('http') ? wheel.spinButtonImage : env + wheel.spinButtonImage) : `${process.env.PUBLIC_URL}/assets/button-placeholder.png`}
              draggable={false}
            />
          </button>
        );

      case 4: // CurvedSlider
        return (
          !mustSpin && (
            <CurvedSlider
              onSpin={handleSpinSlider}
              radius={wheel.wheelDiameter - 50}
            />
          )
        );

      default:
        return null; // For Classic or undefined
    }
  };

  return (
    <>
      <WheelStyles
        wheelDiameter={wheel.wheelDiameter}
      >
        <WheelContainer
          className={openModal ? 'wheel-container disable-clicks' : 'wheel-container'}
          decorated={wheel.spinnerDecoration}
          imageUrl={wheel.spinnerImage ? (wheel.spinnerImage.startsWith('http') ? wheel.spinnerImage : env + wheel.spinnerImage) : `${process.env.PUBLIC_URL}/assets/wheel-decoration.png`}
          spinnerRotate={wheel.spinnerRotate}
          spinnerCoverOpacity={wheel.spinnerCoverOpacity}
          onDragStart={(event) => event.preventDefault()}
          onClick={
            wheel.spinningMethod === 1 && !areAllProbabilitiesZero
              ? handleSpinClick
              : undefined
          }
        >
          <PointerPin
            className={"pointer-pin " + (mustSpin ? "spinning" : "")}
            spinDuration={wheel.spinDuration}
            speedMode={wheel.spinSpeed === 2 ? true : false}
            pinColor={wheel.wheelPinColor || `#eeeeee`}
            bgImage={wheel.wheelPinImage && (wheel.wheelPinImage.startsWith('http') ? wheel.wheelPinImage : env + wheel.wheelPinImage)}
          />
          <img
            src={wheel.circleImage ? (wheel.circleImage.startsWith('http') ? wheel.circleImage : env + wheel.circleImage) : `${process.env.PUBLIC_URL}/assets/eddra-emblem.png`}
            className="center-img"
            alt=""
            draggable={false}
          />
          {!loading && (
            <Wheel            
              mustStartSpinning={mustSpin}
              readyToSpin={readyToSpin}
              onStopSpinning={() => onWheelStop()}
              prizeNumber={prizeNumber}
              spinDuration={wheel.spinDuration / 10}
              data={slicesState.map((slice) => ({
                option: wheel.showPrizeValues ? slice.prizeValue : "",
                ...(slice.wedgeImage && {
                  image: {
                    uri: slice.wedgeImage.startsWith('http') ? slice.wedgeImage : env + slice.wedgeImage,
                    sizeMultiplier: 1.7,
                    offsetY: 80,
                  },
                }),
                probability: slice.probability ? parseFloat(slice.probability.toFixed(3)) : undefined,
              }))}
              startingOptionIndex={0}
              coverImage={wheel.spinnerCover ? (wheel.spinnerCover.startsWith('http') ? wheel.spinnerCover : env + wheel.spinnerCover) : undefined}
              backgroundColors={
                wheel.spinnerCover ? ["transparent"] : [
                wheel.colorFirst || "#fff",
                wheel.colorSecond || "#E60F0F"
              ]}
              textColors={[
                wheel.wheelTextColor || "#3e3e3e",
                wheel.wheelTextColor2 || "#fff",
              ]}
              outerBorderColor={wheel.wheelBorderColor || "#E60F0F"}
              outerBorderWidth={ wheel.spinnerCover ? 0 : 10}
              innerRadius={20}
              radiusLineWidth={wheel.radiusLine ? 5 : 0}
              radiusLineColor={wheel.radiusLineColor || "e60f0f"}
              backwardsAnimation={
                wheel.spinningMethod === 2
                  ? !wheelSlide.clockwise
                  : wheel.spinningMethod === 1 || wheel.spinningMethod === 3
                    ? !wheel.clockwise
                    : undefined
              }
              noAnimationSteps={
                wheel.spinningMethod === 2
                  ? wheelSlide.spinSpeed === 2
                  : wheel.spinningMethod === 1 || wheel.spinningMethod === 3
                    ? wheel.spinSpeed === 2
                    : undefined
              }
              spinnerRotate={wheel.spinnerRotate}
            />
          )}
          {effect && wheel.spinnerEffect && (
            <SpinEffect mustSpin={mustSpin} dataLength={slices.length} />
          )}
        </WheelContainer>
      </WheelStyles>
      {renderSpinMethod()}
      
      <UserDataModal
        open={
          collectUserData === 4
            ? false // remote mode → no modal here
            : (!areAllProbabilitiesZero && isActive && collectUserData === 2 && !dataCollected ? true : openModal)
        }
        onClose={handleClose}
        formState={formState}
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
        submitLabel="Gönder"
      />
    </>
  );
};

export default SpinWheel;
